import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Icon from 'components/DesignSystem/Icon';
import EnsembleDialog from 'components/Meteorology/Weather/Similarity/EnsembleDialog';
import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import useSimilarityMobile from 'hooks/Meteorology/Agro/Similarity/Mobile/useSimilarityMobile';
import {
  setEnsembleModalOpen,
  selectSimilaridadeVariavel,
  selectSimilaridadeMeses,
  selectSimilaridadeEnsembleData,
  selectSimilaridadeEnsembleModalOpen,
} from 'redux/reducers/similaridadeReducer';
import { MapData } from 'types/Meteorology/indicesClimaticos';
import { cssVar } from 'constants/theme';

import ActionBar from './ActionBar';
import ConfigSheet from './ConfigSheet';
import FullscreenMap from './FullscreenMap';
import IndicesSection from './IndicesSection';
import InfoSheet from './InfoSheet';
import MapCarousel from './MapCarousel';
import MONTHS from './months';

import './index.scss';

export default function SimilarityMobile() {
  const dispatch = useDispatch();
  const [configOpen, setConfigOpen] = useState(false);
  const [fullscreenMap, setFullscreenMap] = useState<MapData | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const variable = useSelector(selectSimilaridadeVariavel);
  const [month] = useSelector(selectSimilaridadeMeses);
  const monthLabel = month ? MONTHS.find((m) => m.value === month)?.full : undefined;
  const ensembleModalOpen = useSelector(selectSimilaridadeEnsembleModalOpen);
  const ensembleData = useSelector(selectSimilaridadeEnsembleData);

  const similarity = useSimilarityMobile();

  const setupLabel = [
    variable || 'Selecione a variável',
    monthLabel || 'Selecione o mês',
  ].join(' · ');

  return (
    <div className="agro-simil-mobile">
      <QueryCard>
        <QueryCardHeader
          label="Configuração"
          title={setupLabel}
          action={(
            <button
              type="button"
              aria-label="Como usar a Similaridade"
              className="agro-simil-mobile-info-trigger"
              onClick={() => setInfoOpen(true)}
            >
              <Icon iconType="info" color={cssVar('text-strong')} size="small" />
            </button>
          )}
        />
        <QueryCardTrigger onClick={() => setConfigOpen(true)} />
      </QueryCard>

      {similarity.mapsGenerated && similarity.activeMapsData.length > 0 && (
        <MapCarousel
          maps={similarity.activeMapsData}
          variable={variable}
          onRemove={similarity.toggleYearMap}
          onOpen={setFullscreenMap}
        />
      )}

      <IndicesSection
        isLoading={similarity.isLoading}
        isError={similarity.isError}
        rows={similarity.rows}
        mapsGenerated={similarity.mapsGenerated}
        excludedYears={similarity.excludedYears}
        onToggleMap={similarity.toggleYearMap}
        filters={similarity.filters}
        toggleFilter={similarity.toggleFilter}
        clearFilters={similarity.clearFilters}
        activeFilterCount={similarity.activeFilterCount}
        sortField={similarity.sortField}
        sortDirection={similarity.sortDirection}
        onChangeSort={(field, direction) => {
          similarity.setSortField(field);
          similarity.setSortDirection(direction);
        }}
      />

      <ActionBar
        selectionLabel={similarity.selectionLabel}
        showRegenerate={similarity.mapsGenerated && similarity.rows.length > 0}
        onRegenerate={similarity.handleGenerateMaps}
        canGenerateMaps={similarity.canGenerateMaps}
        isLoadingMaps={similarity.isLoadingMaps}
        onGenerateMaps={similarity.handleGenerateMaps}
        canGenerateEnsemble={similarity.canGenerateEnsemble}
        isGeneratingEnsemble={similarity.isGeneratingEnsemble}
        onGenerateEnsemble={similarity.handleGenerateEnsemble}
      />

      <ConfigSheet open={configOpen} dismiss={() => setConfigOpen(false)} />

      <InfoSheet open={infoOpen} dismiss={() => setInfoOpen(false)} />

      <FullscreenMap
        map={fullscreenMap}
        variable={variable}
        monthLabel={monthLabel}
        dismiss={() => setFullscreenMap(null)}
      />

      <EnsembleDialog
        show={ensembleModalOpen}
        onHide={() => dispatch(setEnsembleModalOpen(false))}
        ensembleData={ensembleData}
      />
    </div>
  );
}
